import {useWorkspaceStore} from '../../../../store/workspaceStore.ts';
import React, {useCallback} from 'react';
import {CircularProgress} from '@mui/material';
import {Collection, CollectionOptionalWorkspace} from '../../../../types.ts';
import {useCollectionStore} from '../../../../store/collectionStore.ts';
import useEffectOnce from '@alchemy/react-hooks/src/useEffectOnce.ts';
import {
    LoadNodeChildren,
    OnNodeUpdate,
    OnSelectionChange,
    TreeNode,
    TreeView,
    TreeViewOptionsProps,
    useTreeState,
    useVirtualNodes,
} from '@alchemy/phrasea-framework';
import {EntityType, WorkspaceOrCollectionTreeItem} from './types.ts';
import CollectionTreeNode from './CollectionTreeNode.tsx';
import {useTranslation} from 'react-i18next';
import CollectionEdit from './CollectionEdit.tsx';
import {EntityName} from '../../../../api/types.ts';
import {createIriFromId, extractIdFromIri} from '@alchemy/api';

export type CollectionTreeData = WorkspaceOrCollectionTreeItem;

type Props<IsMulti extends boolean = false> = {
    onChange?: (
        selection: IsMulti extends true
            ? TreeNode<CollectionTreeData>[]
            : TreeNode<CollectionTreeData> | null
    ) => void;
    workspaceId?: string;
    allowNew?: boolean;
    multiple?: IsMulti;
} & Omit<TreeViewOptionsProps<CollectionTreeData>, 'multiple'>;

export type {Props as CollectionTreeViewProps};

export default function CollectionsTreeView<IsMulti extends boolean = false>({
    workspaceId,
    allowNew,
    multiple,
    onChange,
    defaultSelectedNodes,
    defaultExpandedNodes,
    ...treeViewProps
}: Props<IsMulti>) {
    const {t} = useTranslation();
    const loadWorkspaces = useWorkspaceStore(state => state.load);
    const loadWorkspaceCollections = useCollectionStore(state => state.load);
    const loadCollectionAscendants = useCollectionStore(
        state => state.loadCollectionAscendants
    );
    const loading = useWorkspaceStore(state => state.loading);
    const allWorkspaces = useWorkspaceStore(state => state.workspaces);

    const workspaces = workspaceId
        ? allWorkspaces.filter(w => w.id === workspaceId)
        : allWorkspaces;

    const collectionsTree = useCollectionStore(state => state.tree);

    useEffectOnce(() => {
        loadWorkspaces();
    }, []);

    const loadChildren = useCallback<LoadNodeChildren<CollectionTreeData>>(
        async node => {
            await loadWorkspaceCollections(
                node.data.workspaceId,
                node.data.type === EntityType.Collection
                    ? node.data.id
                    : undefined
            );
        },
        [loadWorkspaceCollections]
    );

    const items = React.useMemo<TreeNode<CollectionTreeData>[]>(() => {
        const mapCollection = (
            collection: CollectionOptionalWorkspace,
            workspaceId: string
        ): TreeNode<CollectionTreeData> => {
            const nodeId = createIriFromId(
                EntityName.Collection,
                collection.id
            );

            const children =
                collectionsTree[collection.id]?.items ?? collection.children;

            return {
                id: nodeId,
                data: {
                    id: collection.id,
                    type: EntityType.Collection,
                    label: collection.displayName,
                    capabilities: collection.capabilities,
                    workspaceId,
                },
                hasChildren: children ? children.length > 0 : false,
                childrenLoaded:
                    !!collectionsTree[collection.id] &&
                    !collectionsTree[collection.id].partial,
                children: children?.map(c => mapCollection(c, workspaceId)),
                canAddChildren: collection.capabilities.createCollection,
            };
        };

        return workspaces.map(w => {
            const nodeId = createIriFromId(EntityName.Workspace, w.id);

            return {
                id: nodeId,
                hasChildren: true,
                data: {
                    id: w.id,
                    type: EntityType.Workspace,
                    label: w.displayName,
                    capabilities: w.capabilities,
                    workspaceId: w.id,
                },
                children: collectionsTree[w.id]?.items.map(c =>
                    mapCollection(c, w.id)
                ),
                childrenLoaded:
                    !!collectionsTree[w.id] && !collectionsTree[w.id].partial,
                canAddChildren: w.capabilities.createCollection,
            };
        });
    }, [workspaces, collectionsTree]);

    const treeStateProps = useTreeState({
        defaultSelectedNodes,
        defaultExpandedNodes,
        resolveExpandedNodes: async selectedNodes => {
            return await Promise.all(
                selectedNodes
                    .filter(iri => iri.startsWith(`/${EntityName.Collection}`))
                    .map(iri => {
                        return loadCollectionAscendants(extractIdFromIri(iri));
                    })
            ).then(collections => {
                return collections
                    .map(c => {
                        const s = [
                            createIriFromId(
                                EntityName.Workspace,
                                c.workspace.id
                            ),
                        ];
                        let ptr: Collection | undefined = c;
                        while ((ptr = ptr.parent)) {
                            s.push(
                                createIriFromId(EntityName.Collection, ptr.id)
                            );
                        }

                        return s;
                    })
                    .flat();
            });
        },
    });
    const {selectedNodes} = treeStateProps;

    const onVirtualNodeUpdate = useCallback<OnNodeUpdate<CollectionTreeData>>(
        (oldNode, newNode) => {
            if (onChange) {
                if (multiple) {
                    if (selectedNodes.some(n => n === oldNode.id)) {
                        onChange(
                            // @ts-expect-error TS can't infer multiple is false here
                            selectedNodes.map(n => {
                                if (n === oldNode.id) {
                                    return {
                                        ...oldNode,
                                        ...newNode,
                                        data: {
                                            ...(oldNode.data ?? {}),
                                            ...(newNode.data ?? {}),
                                        },
                                    };
                                }

                                return items.find(i => i.id === n);
                            })
                        );
                    }
                } else if (
                    selectedNodes[0] &&
                    selectedNodes[0] === oldNode.id
                ) {
                    // @ts-expect-error TS can't infer multiple is false here
                    onChange({
                        ...oldNode,
                        ...newNode,
                        data: {
                            ...(oldNode.data ?? {}),
                            ...(newNode.data ?? {}),
                        },
                    });
                }
            }
        },
        [onChange, items, selectedNodes, multiple]
    );

    const {normalizedNodes, ...editingProps} = useVirtualNodes({
        nodes: items,
        newItem: parentNode => ({
            label: t('collection.tree_view.new_collection', 'New Collection'),
            type: EntityType.Collection,
            capabilities: {
                edit: true,
                createAsset: true,
                createCollection: true,
            },
            workspaceId: parentNode!.data.workspaceId,
        }),
        onVirtualNodeUpdate,
    });

    const onSelectionChange = useCallback<
        OnSelectionChange<CollectionTreeData>
    >(
        selection => {
            // @ts-expect-error TS can't infer multiple is false here
            onChange?.(multiple ? selection : selection[0] || null);
        },
        [onChange]
    );

    if (loading) {
        return <CircularProgress size={50} />;
    }

    return (
        <TreeView
            {...treeViewProps}
            {...treeStateProps}
            loadChildren={loadChildren}
            onSelectionChange={onSelectionChange}
            renderNodeLabel={props => {
                return <CollectionTreeNode {...props} />;
            }}
            multiple={multiple}
            editNodeComponent={CollectionEdit}
            nodes={normalizedNodes}
            {...(allowNew ? editingProps : {})}
        />
    );
}
